import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CloudOff, Server, ShieldCheck, Eye } from "lucide-react";

const reasons = [
{
  icon: CloudOff,
  title: "No Cloud Dependencies",
  description:
  "Your home runs independently. Internet outages don't disable your security or comfort."
},
{
  icon: Server,
  title: "Data Sovereignty",
  description:
  "Every piece of data generated in your home stays physically in your home."
},
{
  icon: ShieldCheck,
  title: "Zero Attack Surface",
  description:
  "No cloud servers means no remote vulnerabilities. Hackers can't breach what doesn't exist."
},
{
  icon: Eye,
  title: "True Privacy",
  description:
  "No company analyzing your routines, no ads based on your home behavior. Complete privacy."
}];


export const PrivacyManifesto = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section
      id="privacy"
      ref={containerRef}
      className="relative py-24 md:py-40 overflow-hidden"
      data-testid="privacy-section">

      {/* Background Image with Parallax */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1771773490670-7376a45c0e96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBibHVlJTIwZGlnaXRhbCUyMHNlY3VyaXR5JTIwc2hpZWxkJTIwbmV0d29yayUyMGNvbm5lY3RlZCUyMGxpbmVzfGVufDB8fHx8MTc3MjU5Mjc1M3ww&ixlib=rb-4.1.0&q=85"
          alt="Abstract network"
          className="w-full h-full object-cover opacity-20" />

        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
      </motion.div>

      {/* Decorative Elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#5e5ce6]/5 blur-3xl" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20">

          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-12 bg-[#5e5ce6]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#5e5ce6] font-medium">
              Our Philosophy
            </span>
            <div className="h-px w-12 bg-[#5e5ce6]" />
          </div>

          <h2
            className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-8 leading-[1.1]"
            data-testid="privacy-headline">

            The Privacy{" "}
            <span className="text-[#5e5ce6]">Manifesto</span>
          </h2>

          <p className="text-base md:text-lg text-white/50 leading-relaxed">
            Big tech wants your home data. They analyze your habits, sell your routines,
            and leave you vulnerable to breaches. We believe there&apos;s a better way.
          </p>
        </motion.div>

        {/* Main Statement */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative glass-card rounded-3xl p-8 md:p-16 mb-16 text-center"
          data-testid="manifesto-statement">

          <p className="text-2xl md:text-4xl font-light text-white/90 leading-relaxed tracking-tight">
            &ldquo;Your smart home should serve{" "}
            <span className="text-[#5e5ce6] font-semibold">you</span>
            , not the other way around.” - Juan Goris, Partner - Fortress Integrations
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason, index) =>
          <motion.div
            key={reason.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group glass-card glass-card-hover rounded-2xl p-8 transition-all duration-300"
            data-testid={`reason-card-${index}`}>

              <div className="flex items-start gap-5">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-[#5e5ce6]/10 border border-[#5e5ce6]/20 flex items-center justify-center group-hover:bg-[#5e5ce6]/20 transition-colors duration-300">
                  <reason.icon className="w-6 h-6 text-[#5e5ce6]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Home Assistant Mention */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16">

          <p className="text-sm text-white/40">
            Powered by{" "}
            <span className="text-[#5e5ce6] font-medium">Home Assistant</span> —
            the world&apos;s most trusted open-source home automation platform
          </p>
        </motion.div>
      </div>
    </section>);

};